import dayjs from 'dayjs';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DateTimeInput } from 'semantic-ui-calendar-react';
import { Form } from 'semantic-ui-react';
import MilkMoneyAccountingService from '~/api/services/milkMoneyAccounting';
import Drawer from '~/components/Drawer';
import { FormFields } from '~/components/FormFields';
import notify from '~/components/Notify';
import { useAuth } from '~/routes/components/AuthProvider';
import { GlobalEl } from '~/styles/app';
import { handleErrors } from '~/utils/helpers';
import { MilkMoneyQuery } from '~/QueryHooks/MilkMoneyQuery';
import { StyledErrorLabel } from './ShowTours/style';
import { UserEl } from '../../DairyFactory/DairyFactoryProfile/Users/style';

interface CreateTourDrawerProps {
  isVisible: boolean;
  handleHiddenDrawer: (shouldRefetch?: boolean) => void;
}

interface TourFormData {
  tourNumber: string;
  driverNumber: string;
  vehicleNumber: string;
  dateStart: string;
  dateEnd: string;
  samplesTotal: string;
  totalAmount: string;
}

const defaultValues: TourFormData = {
  tourNumber: '',
  driverNumber: '',
  vehicleNumber: '',
  dateStart: '',
  dateEnd: '',
  samplesTotal: '',
  totalAmount: ''
};

const CreateTourDrawer: React.FC<CreateTourDrawerProps> = (props) => {
  const { isVisible, handleHiddenDrawer } = props;
  const { t } = useTranslation();
  const { systemData: { languages, logged: { languageId } } } = useAuth();
  const languageLocal = languages.find(language => language.id === languageId);

  const [isBusy, setIsBusy] = useState(false);

  const { handleSubmit, control, reset, setValue, watch } = useForm<TourFormData>({
    mode: 'onSubmit',
    defaultValues
  });

  const watchDateStart = watch('dateStart');
  const watchDateEnd = watch('dateEnd');

  const {
    data: options,
    isLoading: isLoadingOptions
  } = MilkMoneyQuery.optionsDataTour()

  async function onSubmit(data: TourFormData) {
    setIsBusy(true);
    try {
      const formattedData = {
        ...data,
        dateStart: data.dateStart ? dayjs(data.dateStart, 'DD.MM.YYYY HH:mm').format('YYYY-MM-DD HH:mm') : '',
        dateEnd: data.dateEnd ? dayjs(data.dateEnd, 'DD.MM.YYYY HH:mm').format('YYYY-MM-DD HH:mm') : '',
        samplesTotal: parseInt(data.samplesTotal),
        totalAmount: parseFloat(data.totalAmount.toString().replace(',', '.'))
      };

      await MilkMoneyAccountingService.createTour(formattedData);
      notify(t('Tour created successfully'), 'success');
      reset(defaultValues);
      handleHiddenDrawer(true);
    } catch (error) {
      handleErrors(error);
    }
    setIsBusy(false);
  }

  function renderContent() {
    return <>
      <FormFields.Input
        control={control}
        name='tourNumber'
        label={t('Tour no.')}
        placeholder={t('Enter tour no.')}
        rules={{
          required: {
            value: true,
            message: t('Please enter tour no.')
          },
          maxLength: {
            value: 10,
            message: t('Max number of char. is 10')
          }
        }}
      />
      <FormFields.Input
        control={control}
        name='driverNumber'
        label={t('Driver no.')}
        placeholder={t('Enter driver no.')}
        rules={{
          required: {
            value: true,
            message: t('Please enter driver no.')
          },
          maxLength: {
            value: 10,
            message: t('Max number of char. is 10')
          }
        }}
      />
      <FormFields.Input
        control={control}
        name='vehicleNumber'
        label={t('Collection truck no.')}
        placeholder={t('Enter collection truck no.')}
        rules={{
          required: {
            value: true,
            message: t('Please enter collection truck no.')
          },
          maxLength: {
            value: 10,
            message: t('Max number of char. is 10')
          }
        }}
      />
      <Controller
        control={control}
        name='dateStart'
        rules={{
          required: {
            value: true,
            message: t('Please enter tour start date')
          },
          validate: (value) => {
            if (!value) return t('Please enter tour start date');
            const date = dayjs(value, 'DD.MM.YYYY HH:mm');
            if (!date.isValid()) return t('Invalid date format');
            return true;
          }
        }}
        render={({ field, fieldState }) => {
          return <Form.Field required error={!!fieldState.error}>
            <label>{t('Tour start')}</label>
            <DateTimeInput
              placeholder={t('Tour start')}
              closable
              localization={languageLocal?.code}
              minDate={options?.dateFrom ? dayjs(options.dateFrom).format('DD.MM.YYYY') : null}
              closeOnMouseLeave={false}
              value={watchDateStart || ''}
              iconPosition='left'
              dateFormat='DD.MM.YYYY'
              timeFormat='24'
              onChange={(event, { value }) => {
                event.preventDefault();
                event.stopPropagation();
                setValue('dateStart', value as string, { shouldValidate: true });
              }}
            />
            {fieldState.error && <StyledErrorLabel className='ui pointing basic label'>{fieldState.error.message}</StyledErrorLabel>}
          </Form.Field>
        }}
      />
      <Controller
        control={control}
        name='dateEnd'
        rules={{
          required: {
            value: true,
            message: t('Please enter tour end date')
          },
          validate: (value) => {
            if (!value) return t('Please enter tour end date');
            const dateEnd = dayjs(value, 'DD.MM.YYYY HH:mm');
            if (!dateEnd.isValid()) return t('Invalid date format');

            if (watchDateStart) {
              const dateStart = dayjs(watchDateStart, 'DD.MM.YYYY HH:mm');
              if (dateEnd.isBefore(dateStart) || dateEnd.isSame(dateStart)) {
                return t('Tour end must be after tour start');
              }
            }

            const now = dayjs();
            if (dateEnd.isAfter(now)) {
              return t('Tour end must be before or equal to now');
            }

            return true;
          }
        }}
        render={({ field, fieldState }) => {
          return <Form.Field required error={!!fieldState.error}>
            <label>{t('Tour end')}</label>
            <DateTimeInput
              placeholder={t('Please enter tour end date')}
              closable
              localization={languageLocal?.code}
              closeOnMouseLeave={false}
              value={watchDateEnd || ''}
              iconPosition='left'
              dateFormat='DD.MM.YYYY'
              timeFormat='24'
              onChange={(event, { value }) => {
                event.preventDefault();
                event.stopPropagation();
                setValue('dateEnd', value as string, { shouldValidate: true });
              }}
            />
            {fieldState.error && <StyledErrorLabel className='ui pointing basic label'>{fieldState.error.message}</StyledErrorLabel>}
          </Form.Field>
        }}
      />
      <FormFields.Input
        control={control}
        name='samplesTotal'
        label={t('Number of samples')}
        placeholder={t('Enter number of samples')}
        typeInput='controled'
        allowDecimals={false}
        rules={{
          required: {
            value: true,
            message: t('Please enter number of samples')
          },
          validate: (value) => {
            const numValue = parseInt(value);
            if (isNaN(numValue)) return t('Please enter a valid number');
            if (numValue < 0 || numValue > 1000) {
              return t('Number of samples must be between 0 and 1.000');
            }
            return true;
          }
        }}
      />
      <FormFields.Input
        control={control}
        name='totalAmount'
        label={t('Total amount (in l)')}
        placeholder={t('Enter total amount (in l)')}
        typeInput='controled'
        allowDecimals={true}
        decimalScale={2}
        rules={{
          required: {
            value: true,
            message: t('Please enter total amount')
          },
          validate: (value) => {
            const numValue = parseFloat(value.toString().replace(',', '.'));
            if (isNaN(numValue)) return t('Please enter a valid number');
            if (numValue < 0 || numValue > 1000000) {
              return t('Total amount must be between 0 and 1.000.000');
            }
            return true;
          }
        }}
      />
      <UserEl.DrawerFooter>
        <GlobalEl.DefaultOutlineButton type='button' onClick={() => {
          reset(defaultValues);
          handleHiddenDrawer();
        }}>
          {t('Cancel')}
        </GlobalEl.DefaultOutlineButton>
        <GlobalEl.PrimaryButton>
          {t('Create')}
        </GlobalEl.PrimaryButton>
      </UserEl.DrawerFooter>
    </>
  }

  return <Drawer
    onHide={() => {
      reset(defaultValues);
      handleHiddenDrawer();
    }}
    handleSubmit={handleSubmit(onSubmit)}
    isBusy={isBusy || isLoadingOptions}
    header={t('Tour erstellen')}
    content={renderContent()}
    isVisible={isVisible}
    closeOnEscape
  />
}

export default CreateTourDrawer;
